// Task-related TypeScript types

export interface User {
  id: number;
  name: string;
  email?: string;
  initials: string;
}

export interface Project {
  id: number;
  name: string;
}

export interface Epic {
  id: number;
  title: string;
  color?: string;
}

export interface Sprint {
  id: number;
  name: string;
  status: 'planning' | 'active' | 'completed';
}

export interface BoardColumn {
  id: number;
  name: string;
}

export interface Component {
  id: number;
  name: string;
  description?: string;
  active: boolean;
  lead_user?: User;
  task_count?: number;
  created_at: string;
  updated_at: string;
}

export interface Version {
  id: number;
  name: string;
  description?: string;
  status: 'unreleased' | 'released' | 'archived';
  start_date?: string;
  release_date?: string;
  released: boolean;
  archived: boolean;
  progress_percentage: number;
  task_count?: number;
  completed_task_count?: number;
  created_at: string;
  updated_at: string;
}

export interface TaskLink {
  id: number;
  link_type:
    | 'blocks'
    | 'is_blocked_by'
    | 'relates_to'
    | 'duplicates'
    | 'is_duplicated_by'
    | 'clones'
    | 'is_cloned_by';
  direction: 'outgoing' | 'incoming';
  related_task: {
    id: number;
    title: string;
    status: string;
    priority: string;
    item_type: string;
    project: Project;
  };
  created_at: string;
}

export interface TaskComment {
  id: number;
  body: string;
  edited: boolean;
  edited_at?: string;
  created_at: string;
  updated_at: string;
  user: User;
  parent_comment_id?: number;
  replies?: TaskComment[];
  can_edit: boolean;
}

export interface TaskAttachment {
  id: number;
  filename: string;
  content_type?: string;
  file_size?: number;
  human_file_size: string;
  storage_key?: string;
  description?: string;
  is_image: boolean;
  uploaded_by: User;
  can_delete: boolean;
  created_at: string;
}

export interface TaskWatcher {
  id: number;
  name: string;
  email: string;
  initials: string;
}

export interface SredActivityCategory {
  id: number;
  name: string;
}

export type TaskStatus = 'todo' | 'in_progress' | 'completed' | 'blocked';
export type TaskPriority = 'lowest' | 'low' | 'medium' | 'high' | 'highest';
export type TaskItemType = 'story' | 'bug' | 'task' | 'subtask';
export type TaskResolution =
  | 'done'
  | 'wont_do'
  | 'duplicate'
  | 'cannot_reproduce'
  | 'incomplete';

export interface Task {
  id: number;
  title: string;
  name?: string; // alias for backward compatibility
  description?: string;
  status: TaskStatus;
  priority?: TaskPriority | null;
  item_type: TaskItemType;
  story_points?: number;
  position?: number;

  // Dates
  due_date?: string;
  start_date?: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
  resolved_at?: string;

  // Time tracking
  estimated_hours?: number;
  original_estimate?: number;
  time_spent?: number;
  remaining_estimate?: number;
  time_tracking_percentage?: number;

  // Resolution and flags
  resolution?: TaskResolution;
  flagged: boolean;
  environment?: string;

  // Labels
  labels: string[];

  // SR&ED/CDAE
  is_sred_eligible?: boolean;
  is_cdae_eligible?: boolean;
  sred_activity_category?: SredActivityCategory;

  // Relationships
  project?: Project;
  reporter?: User;
  assigned_to?: User;
  epic?: Epic;
  sprint?: Sprint;
  board_column?: BoardColumn;

  // Parent/subtasks
  parent_task_id?: number;
  has_subtasks: boolean;
  subtask_count: number;

  // Components and versions
  components: { id: number; name: string }[];
  fix_versions: { id: number; name: string; released: boolean }[];
  affects_versions: { id: number; name: string; released: boolean }[];

  // Counts and flags
  watcher_count: number;
  is_watching: boolean;
  link_count: number;
  is_blocked: boolean;
  comment_count: number;
  attachment_count: number;

  // Organization
  organization: {
    id: number;
    name: string;
  };
}

export interface TaskFormData {
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  item_type?: TaskItemType;
  project: Project;
  assigned_to_user_id?: number;
  epic_id?: number;
  sprint_id?: number;
  board_column_id?: number;
  parent_task_id?: number;
  due_date?: string;
  start_date?: string;
  story_points?: number;
  estimated_hours?: number;
  original_estimate?: number;
  environment?: string;
  labels?: string[];
  component_ids?: number[];
  fix_version_ids?: number[];
  affects_version_ids?: number[];
}

export const TASK_STATUSES: { value: TaskStatus; label: string }[] = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
];

export const TASK_PRIORITIES: {
  value: TaskPriority;
  label: string;
  color: string;
}[] = [
  { value: 'lowest', label: 'Lowest', color: '#6B7280' },
  { value: 'low', label: 'Low', color: '#3B82F6' },
  { value: 'medium', label: 'Medium', color: '#F59E0B' },
  { value: 'high', label: 'High', color: '#F97316' },
  { value: 'highest', label: 'Highest', color: '#EF4444' },
];

export const TASK_ITEM_TYPES: {
  value: TaskItemType;
  label: string;
  color: string;
}[] = [
  { value: 'story', label: 'Story', color: '#22C55E' },
  { value: 'bug', label: 'Bug', color: '#EF4444' },
  { value: 'task', label: 'Task', color: '#3B82F6' },
  { value: 'subtask', label: 'Sub-task', color: '#6B7280' },
];

export const TASK_RESOLUTIONS: { value: TaskResolution; label: string }[] = [
  { value: 'done', label: 'Done' },
  { value: 'wont_do', label: "Won't Do" },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'cannot_reproduce', label: 'Cannot Reproduce' },
  { value: 'incomplete', label: 'Incomplete' },
];

export const LINK_TYPES: {
  value: string;
  label: string;
  outwardLabel: string;
  inwardLabel: string;
}[] = [
  {
    value: 'blocks',
    label: 'Blocks',
    outwardLabel: 'blocks',
    inwardLabel: 'is blocked by',
  },
  {
    value: 'relates_to',
    label: 'Relates To',
    outwardLabel: 'relates to',
    inwardLabel: 'relates to',
  },
  {
    value: 'duplicates',
    label: 'Duplicates',
    outwardLabel: 'duplicates',
    inwardLabel: 'is duplicated by',
  },
  {
    value: 'clones',
    label: 'Clones',
    outwardLabel: 'clones',
    inwardLabel: 'is cloned by',
  },
];
